import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { message, notification } from 'antd';
import Sheets from './components/Sheets';
import ActionBar from './components/ActionBar';
import apiInstance from '../../api';
import Loader from '../../components/Loader';
import './styles.css';
import Tape from './components/Tape';
import ym from 'react-yandex-metrika';
import GroupedProductsNoticeModal from './components/GroupedProductsNoticeModal';

const GROUPED_PRODUCTS_MODAL_STORAGE_KEY = 'checklist_grouped_products_modal_show_count';
const GROUPED_PRODUCTS_MODAL_SHOW_LIMIT = 3;
const GROUPED_PRODUCTS_MODAL_LOCK_SECONDS = 5;

const CheckListPage = () => {
  const [searchParams] = useSearchParams();
  const { serverTime } = useSelector((store) => store.common); // "2025-07-04 10:40:46"
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [printType, setPrintType] = useState('sheet'); // 'sheet' | 'tape'
  const [isCuttingLineEnabled, setIsCuttingLineEnabled] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isGroupedProductsModalOpen, setIsGroupedProductsModalOpen] = useState(false);
  const [isGroupedProductsModalClosable, setIsGroupedProductsModalClosable] = useState(false);
  const [groupedProductsModalCountdown, setGroupedProductsModalCountdown] = useState(GROUPED_PRODUCTS_MODAL_LOCK_SECONDS);
  const [groupedProductsOrderIndexes, setGroupedProductsOrderIndexes] = useState([]);
  const [hasShownGroupedProductsModalInSession, setHasShownGroupedProductsModalInSession] = useState(false);
  const localData = JSON.parse(localStorage.getItem('checklist'));

  const fetchData = async (endpoint, data) => {
    const api = `${endpoint}?XDEBUG_SESSION_START=MyVSCode`;
    
    try {
      const response = await apiInstance.post(api, JSON.stringify({ data }));
      
      if (response.data.result) {
        return response.data.data;
      }
      
      message.error('Ошибка извлечения данных');
      return null;
    } catch (error) {
      message.error(error.response?.data?.message || 'Ошибка сервера');
      return null;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setIsDataReady(false);
      try {
        if (searchParams.get('accessToken')) {
          const response = await fetchData(
            '/documents/checklist/getOrdersByAccessToken',
            { access_token: searchParams.get('accessToken') }
          );
          if (response) setData(response);
        } else if (localData) {
          const response = await fetchData(
            '/documents/checklist/getOrders',
            {
              requestTimestamp: Date.now(),
              kitchenId: localData.kitchenId,
              kitchenRacePolygonIds: localData.kitchenRacePolygonIds,
              deliveryDay: localData.deliveryDay,
            }
          );
          if (response) setData(response.orders);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
        setIsLoading(false);
      } finally {
        setIsLoading(false);
        setIsDataReady(true);
      }
    };

    loadData();
  }, [searchParams]);

  const hasAccessToken = !!(searchParams.get('accessToken') && searchParams.get('accessToken').trim());
  const hasSelected = Array.isArray(localData?.selectedRowKeys) && localData.selectedRowKeys.length > 0;

  const filteredData = !hasAccessToken && hasSelected
    ? data.filter(item =>
        localData.selectedRowKeys.includes(item.id) ||
        localData.selectedRowKeys.includes(String(item.id))
      )
    : data;

  useEffect(() => {
    if (!isDataReady) {
      return;
    }

    const orderIndexes = filteredData.reduce((indexes, order, index) => {
      const hasGroupedProducts = Array.isArray(order?.products)
        && order.products.some((product) => Number(product?.amount) > 4);

      if (hasGroupedProducts) {
        indexes.push(index + 1);
      }

      return indexes;
    }, []);

    if (orderIndexes.length === 0) {
      setGroupedProductsOrderIndexes([]);
      return;
    }

    // Всегда записываем индексы заказов — чтобы ActionBar получал их
    // даже если модальное окно больше не показывается.
    setGroupedProductsOrderIndexes(orderIndexes);

    const shownCount = Number(localStorage.getItem(GROUPED_PRODUCTS_MODAL_STORAGE_KEY) || 0);

    // Показываем модальное окно только если оно ещё не показывалось в сессии,
    // не открыто сейчас и количество показов в хранилище меньше лимита.
    if (!hasShownGroupedProductsModalInSession && !isGroupedProductsModalOpen && shownCount < GROUPED_PRODUCTS_MODAL_SHOW_LIMIT) {
      localStorage.setItem(GROUPED_PRODUCTS_MODAL_STORAGE_KEY, String(shownCount + 1));
      setGroupedProductsModalCountdown(GROUPED_PRODUCTS_MODAL_LOCK_SECONDS);
      setIsGroupedProductsModalClosable(false);
      setIsGroupedProductsModalOpen(true);
      setHasShownGroupedProductsModalInSession(true);
    }
  }, [
    filteredData,
    hasShownGroupedProductsModalInSession,
    isDataReady,
    isGroupedProductsModalOpen,
  ]);

  useEffect(() => {
    if (!isGroupedProductsModalOpen || isGroupedProductsModalClosable) {
      return;
    }

    if (groupedProductsModalCountdown <= 0) {
      setIsGroupedProductsModalClosable(true);
      return;
    }

    const timer = setTimeout(() => {
      setGroupedProductsModalCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    groupedProductsModalCountdown,
    isGroupedProductsModalClosable,
    isGroupedProductsModalOpen,
  ]);

  const handleCloseGroupedProductsModal = () => {
    if (!isGroupedProductsModalClosable) {
      return;
    }

    setIsGroupedProductsModalOpen(false);
  };
    
  const handleMark = async () => {
    if (localData && filteredData.length > 0) {
      const response = await fetchData(
        '/documents/checklist/markOrdersAsPrinted',
        {
          requestTimestamp: Date.now(),
          kitchenId: localData.kitchenId,
          orderIds: filteredData.map(item => item.id),
        }
      );
      console.log('Mark response:', response);
      ym('reachGoal','checklist_print');
    }
  }
  
  useEffect(() => {
    if (localData && localData.deliveryDay) {
      const deliveryDay = localData.deliveryDay; // expected format: YYYY-MM-DD
      const serverDate = serverTime ? moment(serverTime).format('YYYY-MM-DD') : null;
      const clientDate = moment().format('YYYY-MM-DD');
      const referenceDate = serverDate || clientDate;

      if (deliveryDay !== referenceDate) {
        notification.warning({
          message: 'Внимание',
          description: `Вы смотрите заказы не на сегодня, а на ${deliveryDay}. Текущая дата: ${serverDate || clientDate}`,
          duration: 0,
        });
      }
    }
  }, []);

  // useEffect(() => {
  //   if (localData && filteredData && filteredData.length > 0) {
  //     const markData = async () => {
  //       try {
  //         const payload = {
  //           kitchenId: localData.kitchenId,
  //           orderIds: filteredData.map(item => item.id),
  //         };

  //         const response = await apiInstance.post('/documents/checklist/markOrdersAsPrinted', JSON.stringify(payload));
  //         console.log('Mark response:', response?.data || response);
  //       } catch (err) {
  //         console.error('Error marking orders as printed:', err);
  //       }
  //     };
  //     markData();
  //     try { ym('reachGoal','checklist_print'); } catch (e) { /* ignore */ }
  //   }
  // }, [localData, filteredData]);

  const handleDownloadDriver = () => {
    // Создаем ссылку для скачивания драйвера из папки public
    const link = document.createElement('a');
    link.href = '/POS Printer Driver Setup V7.17.exe';
    link.download = 'POS Printer Driver Setup V7.17.exe';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="checklist-wrapper">
      {isLoading && <Loader/>}
      <GroupedProductsNoticeModal
        open={isGroupedProductsModalOpen}
        onClose={handleCloseGroupedProductsModal}
        orderIndexes={groupedProductsOrderIndexes}
        canClose={isGroupedProductsModalClosable}
        countdown={groupedProductsModalCountdown}
      />
      <ActionBar
        orderIndexes={groupedProductsOrderIndexes}
        openGroupModal={() => setIsGroupedProductsModalOpen(true)}
        printType={printType} 
        onMark={handleMark}
        setPrintType={setPrintType}
        handleDownloadDriver={handleDownloadDriver}
        filteredDataLength={filteredData.length}
        isCuttingLineEnabled={isCuttingLineEnabled}
        setIsCuttingLineEnabled={setIsCuttingLineEnabled}
      />
      {filteredData.length === 0 && !isLoading ?
        <div className="checklist-no-data">Нет данных</div>
      :
        <>
          {printType === 'tape' ? 
            <Tape data={filteredData} isCuttingLineEnabled={isCuttingLineEnabled}/>
          :
            <div className="mobile-wrapper">
              <Sheets data={filteredData}/>
            </div>
          }
        </>
      }
    </div>
  );
};

export default CheckListPage;